import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ThemeToggle from '../../components/ThemeToggle';

function MasterAdminEmails() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Form state
  const [recipientType, setRecipientType] = useState('single'); // single, all, tier, status
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const token = localStorage.getItem('masterAdminToken');
  const user = JSON.parse(localStorage.getItem('masterAdminUser') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/master-admin/login');
      return;
    }
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const fetchTenants = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/tenants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('masterAdminToken');
        navigate('/master-admin/login');
        return;
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setTenants(data.tenants || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('masterAdminToken');
    localStorage.removeItem('masterAdminUser');
    navigate('/master-admin/login');
  };

  const getRecipientDescription = () => {
    switch (recipientType) {
      case 'single':
        const tenant = tenants.find(t => t.id === parseInt(selectedTenantId));
        return tenant ? `${tenant.companyName} (${tenant.contactEmail})` : 'Select a tenant';
      case 'all':
        return `All tenants (${tenants.length})`;
      case 'tier':
        const tierTenants = tenants.filter(t => t.subscriptionTier === selectedTier);
        return `All ${selectedTier.replace('_', ' ')} tenants (${tierTenants.length})`;
      case 'status':
        const statusTenants = tenants.filter(t => t.subscriptionStatus === selectedStatus);
        return `All ${selectedStatus.replace('_', ' ')} tenants (${statusTenants.length})`;
      default:
        return 'Unknown';
    }
  };

  const getRecipientCount = () => {
    switch (recipientType) {
      case 'single':
        return selectedTenantId ? 1 : 0;
      case 'all':
        return tenants.length;
      case 'tier':
        return tenants.filter(t => t.subscriptionTier === selectedTier).length;
      case 'status':
        return tenants.filter(t => t.subscriptionStatus === selectedStatus).length;
      default:
        return 0;
    }
  };

  const isFormValid = () => {
    if (!subject.trim() || !message.trim()) return false;
    if (recipientType === 'single' && !selectedTenantId) return false;
    if (recipientType === 'tier' && !selectedTier) return false;
    if (recipientType === 'status' && !selectedStatus) return false;
    return getRecipientCount() > 0;
  };

  const handlePreview = () => {
    if (isFormValid()) {
      setShowPreview(true);
    }
  };

  const handleSend = async () => {
    if (!isFormValid()) return;

    setSending(true);
    setError('');
    setSuccess('');

    try {
      let body = { subject, message };

      if (recipientType === 'single') {
        body.tenantIds = [parseInt(selectedTenantId)];
      } else if (recipientType === 'all') {
        body.filter = 'all';
      } else if (recipientType === 'tier') {
        body.filter = `tier:${selectedTier}`;
      } else if (recipientType === 'status') {
        body.filter = `status:${selectedStatus}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      setSuccess(`Email sent successfully to ${data.successCount} recipient(s).${data.failureCount > 0 ? ` ${data.failureCount} failed.` : ''}`);
      setShowPreview(false);

      // Reset form
      setSubject('');
      setMessage('');
      setRecipientType('single');
      setSelectedTenantId('');
      setSelectedTier('');
      setSelectedStatus('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page master-admin-page">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page master-admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>DeedStack Platform Admin</h1>
        </div>
        <div className="admin-header-right">
          <span className="admin-user-name">{user.firstName} {user.lastName}</span>
          <ThemeToggle />
          <button onClick={handleLogout} className="btn btn-secondary btn-small">
            Logout
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="admin-nav">
        <Link to="/master-admin/dashboard" className="admin-nav-link">Dashboard</Link>
        <Link to="/master-admin/tenants" className="admin-nav-link">Tenants</Link>
        <Link to="/master-admin/calendar" className="admin-nav-link">Calendar</Link>
        <Link to="/master-admin/emails" className="admin-nav-link active">Emails</Link>
        <Link to="/master-admin/billing" className="admin-nav-link">Billing</Link>
        <Link to="/master-admin/financials" className="admin-nav-link">Financials</Link>
      </nav>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-container">
          <div className="admin-section">
            <h2>Send Email to Tenants</h2>

            {error && (
              <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            {success && (
              <div className="alert alert-success" style={{
                marginBottom: '1rem',
                backgroundColor: 'var(--color-success-bg)',
                border: '1px solid var(--color-success-border)',
                borderRadius: '6px',
                padding: '12px 16px',
                color: 'var(--color-success-text)'
              }}>
                {success}
              </div>
            )}

            {/* Compose Form */}
            <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
              <h3>Recipients</h3>

              {/* Recipient Type Selection */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Send to:
                </label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="recipientType"
                      value="single"
                      checked={recipientType === 'single'}
                      onChange={(e) => setRecipientType(e.target.value)}
                    />
                    Single Tenant
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="recipientType"
                      value="all"
                      checked={recipientType === 'all'}
                      onChange={(e) => setRecipientType(e.target.value)}
                    />
                    All Tenants
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="recipientType"
                      value="tier"
                      checked={recipientType === 'tier'}
                      onChange={(e) => setRecipientType(e.target.value)}
                    />
                    By Tier
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="recipientType"
                      value="status"
                      checked={recipientType === 'status'}
                      onChange={(e) => setRecipientType(e.target.value)}
                    />
                    By Status
                  </label>
                </div>
              </div>

              {/* Single Tenant Selector */}
              {recipientType === 'single' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Select Tenant:
                  </label>
                  <select
                    value={selectedTenantId}
                    onChange={(e) => setSelectedTenantId(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      fontSize: '1rem',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">-- Select a tenant --</option>
                    {tenants.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.companyName} ({tenant.contactEmail})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Tier Selector */}
              {recipientType === 'tier' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Select Tier:
                  </label>
                  <select
                    value={selectedTier}
                    onChange={(e) => setSelectedTier(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      fontSize: '1rem',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">-- Select a tier --</option>
                    <option value="free_trial">Free Trial ({tenants.filter(t => t.subscriptionTier === 'free_trial').length})</option>
                    <option value="starter">Starter ({tenants.filter(t => t.subscriptionTier === 'starter').length})</option>
                    <option value="pro">PRO ({tenants.filter(t => t.subscriptionTier === 'pro').length})</option>
                    <option value="enterprise">Enterprise ({tenants.filter(t => t.subscriptionTier === 'enterprise').length})</option>
                  </select>
                </div>
              )}

              {/* Status Selector */}
              {recipientType === 'status' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Select Status:
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      fontSize: '1rem',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">-- Select a status --</option>
                    <option value="trial">Trial ({tenants.filter(t => t.subscriptionStatus === 'trial').length})</option>
                    <option value="active">Active ({tenants.filter(t => t.subscriptionStatus === 'active').length})</option>
                    <option value="past_due">Past Due ({tenants.filter(t => t.subscriptionStatus === 'past_due').length})</option>
                    <option value="suspended">Suspended ({tenants.filter(t => t.subscriptionStatus === 'suspended').length})</option>
                  </select>
                </div>
              )}

              {/* Recipient Summary */}
              <div style={{
                backgroundColor: 'var(--color-surface-sunken)',
                padding: '0.75rem 1rem',
                borderRadius: '4px',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                <strong>Recipients:</strong> {getRecipientDescription()}
              </div>
            </div>

            {/* Email Content */}
            <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
              <h3>Email Content</h3>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Subject:
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid var(--color-border)',
                    fontSize: '1rem',
                    minHeight: '44px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Message:
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Enter your message...

You can use basic formatting:
- **bold text** for emphasis
- Line breaks will be preserved
- Lists work naturally"
                  rows={10}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid var(--color-border)',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                <p style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', marginTop: '0.5rem' }}>
                  Tip: Use **text** for bold. Line breaks will be preserved in the email.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handlePreview}
                disabled={!isFormValid()}
                className="btn btn-secondary"
                style={{ opacity: isFormValid() ? 1 : 0.5 }}
              >
                Preview Email
              </button>
              <button
                onClick={handleSend}
                disabled={!isFormValid() || sending}
                className="btn btn-primary"
                style={{ opacity: isFormValid() && !sending ? 1 : 0.5 }}
              >
                {sending ? 'Sending...' : `Send to ${getRecipientCount()} recipient(s)`}
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Preview Modal */}
      {showPreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            padding: '1.5rem'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Email Preview</h3>

            <div style={{ marginBottom: '1rem' }}>
              <strong>To:</strong> {getRecipientDescription()}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <strong>Subject:</strong> {subject}
            </div>

            <div style={{
              backgroundColor: 'var(--color-surface-sunken)',
              padding: '1rem',
              borderRadius: '4px',
              border: '1px solid var(--color-border)',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '1rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid var(--color-border)'
              }}>
                <img
                  src="/images/deedstack-logos/logo-horizontal-final.svg"
                  alt="DeedStack"
                  style={{ height: '40px' }}
                />
              </div>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                {message.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}
              </div>
              <div style={{
                marginTop: '1.5rem',
                paddingTop: '1rem',
                borderTop: '1px solid var(--color-border)',
                textAlign: 'center',
                fontSize: '0.85rem',
                color: 'var(--color-text-light)'
              }}>
                <p style={{ margin: 0 }}>DeedStack - Land Financing Platform</p>
                <p style={{ margin: '0.25rem 0 0 0' }}>support@deedstack.com</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPreview(false)}
                className="btn btn-secondary"
              >
                Edit
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="btn btn-primary"
              >
                {sending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MasterAdminEmails;
